import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { useTheme } from '../../context/ThemeContext';
import { supabase } from '../../lib/supabase';
import CardImage from '../../components/CardImage';

// Icons
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

const MessageCircleIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

// Chase's system user ID (for filtering system messages)
const CHASE_ID = '550e8400-e29b-41d4-a716-446655440000';

// Helper to get current user ID from users table
const getCurrentUserId = async () => {
  const { data } = await supabase.auth.getSession();
  const authId = data?.session?.user?.id;
  if (!authId) return null;

  const { data: userData, error } = await supabase
    .from('users')
    .select('id')
    .eq('auth_id', authId)
    .single();

  if (error || !userData) return null;
  return userData.id;
};

// Fetch real conversations from database (grouped by partner)
const fetchRealConversations = async (userId) => {
  // Get accepted trade requests where user is involved
  const { data: trades, error } = await supabase
    .from('trade_requests')
    .select(`
      id,
      requester_id,
      card_owner_id,
      accepted_at,
      last_read_requester_at,
      last_read_owner_at,
      cards (name, digital_image_url),
      requester:users!trade_requests_requester_id_fkey (id, username, avatar_url),
      card_owner:users!trade_requests_card_owner_id_fkey (id, username, avatar_url),
      trade_schedules (status)
    `)
    .or(`requester_id.eq.${userId},card_owner_id.eq.${userId}`)
    .eq('status', 'accepted')
    .order('accepted_at', { ascending: false });

  if (error) {
    console.error('Error fetching trades:', error);
    return [];
  }

  console.log('Messages: Raw trades fetched:', trades?.length, trades);

  // Group ALL trades by partner (no filtering - show confirmed trades too)
  const groupedByPartner = {};
  (trades || []).forEach(trade => {
    const isRequester = trade.requester_id === userId;
    const partnerId = isRequester ? trade.card_owner_id : trade.requester_id;

    if (!groupedByPartner[partnerId]) {
      groupedByPartner[partnerId] = {
        trades: [],
        partner: isRequester ? trade.card_owner : trade.requester,
        isRequester,
      };
    }
    groupedByPartner[partnerId].trades.push(trade);
  });

  console.log('Messages: Grouped by partner:', Object.keys(groupedByPartner).length);

  // Build conversations from grouped data
  const conversations = await Promise.all(Object.entries(groupedByPartner).map(async ([partnerId, group]) => {
    const { trades, partner, isRequester } = group;

    // Sort trade IDs for consistent primary selection (same as Chat.js)
    const sortedTrades = [...trades].sort((a, b) => a.id.localeCompare(b.id));
    const primaryTrade = sortedTrades[0];
    const allTradeIds = trades.map(t => t.id);

    const partnerName = partner?.username || 'Trader';

    // Collect all cards in this grouped trade
    const cards = trades.map(t => ({
      name: t.cards?.name || 'Unknown Card',
      image: t.cards?.digital_image_url || '',
    }));

    // Get last message from ANY of the trade threads (they might share messages)
    const { data: messages } = await supabase
      .from('messages')
      .select('message, created_at, sender_id')
      .in('trade_request_id', allTradeIds)
      .eq('is_ignored', false)
      .order('created_at', { ascending: false })
      .limit(1);

    const lastMsg = messages?.[0];
    const lastMessageText = lastMsg?.message || 'Start chatting...';
    const lastMessageTime = lastMsg?.created_at ? new Date(lastMsg.created_at) : new Date(primaryTrade.accepted_at);

    // Calculate unread count - messages from partner after our last_read timestamp
    const lastReadAt = isRequester
      ? primaryTrade.last_read_requester_at
      : primaryTrade.last_read_owner_at;

    let unreadCount = 0;
    if (allTradeIds.length > 0) {
      let unreadQuery = supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .in('trade_request_id', allTradeIds)
        .eq('is_ignored', false)
        .neq('sender_id', userId);

      if (lastReadAt) {
        unreadQuery = unreadQuery.gt('created_at', lastReadAt);
      }

      const { count } = await unreadQuery;
      unreadCount = count || 0;
    }

    // Calculate urgency based on earliest accepted_at
    const earliestAccepted = trades.reduce((earliest, t) => {
      const acceptedAt = new Date(t.accepted_at || Date.now());
      return acceptedAt < earliest ? acceptedAt : earliest;
    }, new Date());

    const now = new Date();
    const hoursSinceAccepted = (now - earliestAccepted) / (1000 * 60 * 60);
    const hoursLeft = Math.max(0, 72 - hoursSinceAccepted);

    let urgency = '3_days';
    if (hoursLeft <= 0) urgency = 'expired';
    else if (hoursLeft <= 24) urgency = 'last_day';
    else if (hoursLeft <= 48) urgency = '2_days';

    // Card display: show first card name, add count if multiple
    const cardDisplay = cards.length > 1
      ? `${cards[0].name} +${cards.length - 1} more`
      : cards[0].name;

    return {
      id: primaryTrade.id,
      tradeRequestId: primaryTrade.id,
      allTradeIds,
      partnerName,
      partnerAvatar: partner?.avatar_url || null,
      cardName: cardDisplay,
      cardImage: cards[0].image,
      cards, // All cards for fan display
      cardCount: cards.length,
      lastMessage: lastMessageText.length > 50 ? lastMessageText.substring(0, 50) + '...' : lastMessageText,
      lastMessageTime,
      unreadCount,
      urgency,
      hoursLeft,
      startedAt: earliestAccepted, // When the trade was accepted
    };
  }));

  // Sort by last message time (most recent first)
  conversations.sort((a, b) => b.lastMessageTime - a.lastMessageTime);

  return conversations;
};

// Filter config
const FILTER_CONFIG = {
  last_day: {
    title: 'Last Day',
    subtitle: 'Expiring within 24 hours',
    color: '#ff4444',
    bgColor: 'rgba(255, 68, 68, 0.1)',
    borderColor: 'rgba(255, 68, 68, 0.3)',
  },
  '2_days': {
    title: '2 Days Left',
    subtitle: 'Expiring within 48 hours',
    color: '#ff8c66',
    bgColor: 'rgba(255, 140, 102, 0.1)',
    borderColor: 'rgba(255, 140, 102, 0.3)',
  },
  '3_days': {
    title: '3 Days Left',
    subtitle: 'Start planning',
    color: '#0ea5e9',
    bgColor: 'rgba(14, 165, 233, 0.1)',
    borderColor: 'rgba(14, 165, 233, 0.3)',
  },
  expired: {
    title: 'Expired',
    subtitle: 'Past 3-day window',
    color: '#888888',
    bgColor: 'rgba(136, 136, 136, 0.1)',
    borderColor: 'rgba(136, 136, 136, 0.3)',
  },
  all: {
    title: 'All',
    subtitle: 'All trades being scheduled',
    color: '#0ea5e9',
    bgColor: 'transparent',
  },
};

// Conversation Item Component
const ConversationItem = ({ conversation, filterColor, onClick, theme }) => {
  const formatTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const formatHoursLeft = (hours, urgency, startedAt) => {
    // Format start date like "Jan 5"
    const startDate = startedAt ? new Date(startedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

    if (urgency === 'expired') {
      return startDate ? `Started ${startDate} - Expired` : 'Expired';
    }

    const timeLeft = hours < 1
      ? 'Expiring soon!'
      : hours < 24
        ? `${Math.floor(hours)}h left`
        : `${Math.floor(hours / 24)}d ${Math.floor(hours % 24)}h left`;

    return startDate ? `Started ${startDate} - ${timeLeft}` : timeLeft;
  };

  // Get cards array for fan display
  const cards = conversation.cards || [{ image: conversation.cardImage, name: conversation.cardName }];
  const hasMultiple = cards.length > 1;

  // Render fanned cards
  const renderCardFan = (itemStyles) => {
    const visibleCards = cards.slice(0, 3);
    return (
      <div style={styles.cardFanContainer}>
        {visibleCards.map((card, index) => (
          <CardImage
            key={index}
            digitalImage={card.image || card.cardImage}
            name={card.name || card.cardName}
            theme={theme}
            style={{
              ...styles.cardImage,
              ...styles.fannedCard,
              backgroundColor: theme.border,
              zIndex: 3 - index,
              transform: `translateX(${index * 10}px) rotate(${index * 4}deg)`,
            }}
          />
        ))}
        {cards.length > 3 && (
          <div style={itemStyles.moreCardsIndicator}>
            <span style={styles.moreCardsText}>+{cards.length - 3}</span>
          </div>
        )}
      </div>
    );
  };

  // Create themed styles for this item
  const itemStyles = {
    conversationItem: {
      ...styles.conversationItem,
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
    },
    cardImage: {
      ...styles.cardImage,
      backgroundColor: theme.border,
    },
    unreadDot: {
      ...styles.unreadDot,
      backgroundColor: theme.accent,
      border: `2px solid ${theme.bgCard}`,
    },
    partnerName: {
      ...styles.partnerName,
      color: theme.text,
    },
    timeAgo: {
      ...styles.timeAgo,
      color: theme.textSecondary,
    },
    cardName: {
      ...styles.cardName,
      color: theme.accent,
    },
    lastMessage: {
      ...styles.lastMessage,
      color: theme.textMuted,
    },
    conversationRight: {
      ...styles.conversationRight,
      color: theme.textSecondary,
    },
    unreadBadge: {
      ...styles.unreadBadge,
      backgroundColor: theme.accent,
    },
    moreCardsIndicator: {
      ...styles.moreCardsIndicator,
      backgroundColor: theme.accent,
    },
  };

  return (
    <button style={itemStyles.conversationItem} onClick={onClick}>
      {/* Card Image(s) - Fan if multiple */}
      <div style={styles.cardImageContainer}>
        {hasMultiple ? (
          renderCardFan(itemStyles)
        ) : (
          <CardImage
            digitalImage={conversation.cardImage}
            name={conversation.cardName}
            theme={theme}
            style={itemStyles.cardImage}
          />
        )}
        {conversation.unreadCount > 0 && (
          <div style={itemStyles.unreadDot} />
        )}
      </div>

      {/* Conversation Info */}
      <div style={styles.conversationInfo}>
        <div style={styles.conversationHeader}>
          <span style={itemStyles.partnerName}>{conversation.partnerName}</span>
          <span style={itemStyles.timeAgo}>
            {conversation.urgency === 'expired' ? `Last message: ${formatTime(conversation.lastMessageTime)}` : formatTime(conversation.lastMessageTime)}
          </span>
        </div>
        <span style={itemStyles.cardName}>{conversation.cardName}</span>
        <span style={itemStyles.lastMessage} title={conversation.lastMessage}>
          {conversation.lastMessage}
        </span>
        <span style={{ ...styles.expiryTime, color: filterColor }}>
          {formatHoursLeft(conversation.hoursLeft, conversation.urgency, conversation.startedAt)}
        </span>
      </div>

      {/* Unread Badge & Arrow */}
      <div style={itemStyles.conversationRight}>
        {conversation.unreadCount > 0 && (
          <div style={itemStyles.unreadBadge}>
            <span style={styles.unreadText}>{conversation.unreadCount}</span>
          </div>
        )}
        <ChevronRightIcon />
      </div>
    </button>
  );
};

// Filter Tab Component
const FilterTab = ({ filter, isActive, count, onClick, theme }) => {
  const config = FILTER_CONFIG[filter];

  return (
    <button
      style={{
        ...styles.filterTab,
        backgroundColor: isActive ? config.color : 'transparent',
        borderColor: isActive ? config.color : theme.border,
      }}
      onClick={onClick}
    >
      <span style={{
        ...styles.filterTabText,
        color: isActive ? '#fff' : theme.textMuted,
      }}>
        {config.title}
      </span>
      {count > 0 && (
        <span style={{
          ...styles.filterTabCount,
          backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : theme.border,
          color: isActive ? '#fff' : theme.text,
        }}>
          {count}
        </span>
      )}
    </button>
  );
};

// Empty State Component
const EmptyState = ({ filter }) => {
  const config = FILTER_CONFIG[filter];

  return (
    <div style={styles.emptyState}>
      <div style={{ color: '#666666' }}>
        <MessageCircleIcon />
      </div>
      <span style={styles.emptyTitle}>Nothing in progress</span>
      <span style={styles.emptySubtitle}>
        {filter === 'last_day'
          ? 'No trades expiring today'
          : filter === '2_days'
          ? 'No trades due in the next 2 days'
          : filter === 'expired'
          ? 'No expired trades'
          : 'Send request to start scheduling'}
      </span>
    </div>
  );
};

// Main Messages Component
const Messages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { theme, isDark } = useTheme();

  const initialFilter = searchParams.get('filter') || 'all';
  const shouldAutoFocusSearch = searchParams.get('search') === 'true';
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(shouldAutoFocusSearch);

  useEffect(() => {
    const loadConversations = async () => {
      // Use activeProfile.id (kid's ID when parent views as kid)
      if (!activeProfile?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const data = await fetchRealConversations(activeProfile.id);
      setConversations(data);
      setLoading(false);
    };

    loadConversations();
  }, [user, activeProfile?.id]);

  // Filter conversations based on active filter and search
  const filteredConversations = conversations.filter(c => {
    // Filter by urgency
    const matchesFilter = activeFilter === 'all' || c.urgency === activeFilter;
    // Filter by search query
    const query = searchQuery.toLowerCase().trim();
    if (!query) return matchesFilter;

    // Check partner name
    if (c.partnerName?.toLowerCase().includes(query)) return matchesFilter;

    // Check all cards in the fan (not just the display string)
    const cards = c.cards || [];
    const matchesAnyCard = cards.some(card =>
      card.name?.toLowerCase().includes(query)
    );
    if (matchesAnyCard) return matchesFilter;

    return false;
  });

  // Count per filter
  const counts = {
    all: conversations.length,
    last_day: conversations.filter(c => c.urgency === 'last_day').length,
    '2_days': conversations.filter(c => c.urgency === '2_days').length,
    '3_days': conversations.filter(c => c.urgency === '3_days').length,
    expired: conversations.filter(c => c.urgency === 'expired').length,
  };

  const handleBack = () => {
    navigate('/chat');
  };

  const handleConversationClick = (conversation) => {
    // Navigate to chat screen (not implemented yet)
    // For now, just log it
    console.log('Opening chat:', conversation.tradeRequestId);
    navigate(`/chat/${conversation.tradeRequestId}`);
  };

  const filterConfig = FILTER_CONFIG[activeFilter];

  // Create theme-aware styles
  const themedStyles = {
    container: {
      ...styles.container,
      backgroundColor: theme.bg,
    },
    header: {
      ...styles.header,
      backgroundColor: theme.bg,
      borderBottom: `1px solid ${theme.bgCard}`,
    },
    backButton: {
      ...styles.backButton,
      color: theme.accent,
    },
    headerTitle: {
      ...styles.headerTitle,
      color: theme.text,
    },
    searchInputContainer: {
      ...styles.searchInputContainer,
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
    },
    searchInput: {
      ...styles.searchInput,
      color: theme.text,
    },
    clearSearchBtn: {
      ...styles.clearSearchBtn,
      color: theme.textSecondary,
    },
    searchToggleBtn: {
      ...styles.searchToggleBtn,
      color: theme.accent,
    },
    conversationItem: {
      ...styles.conversationItem,
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
    },
    cardImage: {
      ...styles.cardImage,
      backgroundColor: theme.border,
    },
    unreadDot: {
      ...styles.unreadDot,
      backgroundColor: theme.accent,
      border: `2px solid ${theme.bgCard}`,
    },
    partnerName: {
      ...styles.partnerName,
      color: theme.text,
    },
    timeAgo: {
      ...styles.timeAgo,
      color: theme.textSecondary,
    },
    cardName: {
      ...styles.cardName,
      color: theme.accent,
    },
    lastMessage: {
      ...styles.lastMessage,
      color: theme.textMuted,
    },
    conversationRight: {
      ...styles.conversationRight,
      color: theme.textSecondary,
    },
    unreadBadge: {
      ...styles.unreadBadge,
      backgroundColor: theme.accent,
    },
    emptyTitle: {
      ...styles.emptyTitle,
      color: theme.text,
    },
    emptySubtitle: {
      ...styles.emptySubtitle,
      color: theme.textMuted,
    },
    skeletonItem: {
      ...styles.skeletonItem,
      backgroundColor: theme.bgCard,
    },
    skeletonImage: {
      ...styles.skeletonImage,
      backgroundColor: theme.border,
    },
    skeletonLine: {
      ...styles.skeletonLine,
      backgroundColor: theme.border,
    },
    moreCardsIndicator: {
      ...styles.moreCardsIndicator,
      backgroundColor: theme.accent,
    },
  };

  return (
    <div style={themedStyles.container}>
      {/* Header */}
      <div style={themedStyles.header}>
        <button style={themedStyles.backButton} onClick={handleBack}>
          <ArrowLeftIcon />
        </button>
        <div style={styles.headerContent}>
          {searchOpen ? (
            <div style={themedStyles.searchInputContainer}>
              <SearchIcon />
              <input
                type="text"
                placeholder="Search by name or card..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={themedStyles.searchInput}
                autoFocus
              />
              {searchQuery && (
                <button
                  style={themedStyles.clearSearchBtn}
                  onClick={() => setSearchQuery('')}
                >
                  <CloseIcon />
                </button>
              )}
            </div>
          ) : (
            <>
              <h1 style={themedStyles.headerTitle}>Messages</h1>
              <span style={{ ...styles.headerSubtitle, color: filterConfig.color }}>
                {filterConfig.subtitle}
              </span>
            </>
          )}
        </div>
        <button
          style={themedStyles.searchToggleBtn}
          onClick={() => {
            setSearchOpen(!searchOpen);
            if (searchOpen) setSearchQuery('');
          }}
        >
          {searchOpen ? <CloseIcon /> : <SearchIcon />}
        </button>
      </div>

      {/* Filter Tabs */}
      <div style={styles.filterTabs}>
        {['all', 'last_day', '2_days', '3_days', 'expired'].map((filter) => (
          <FilterTab
            key={filter}
            filter={filter}
            isActive={activeFilter === filter}
            count={counts[filter]}
            onClick={() => setActiveFilter(filter)}
            theme={theme}
          />
        ))}
      </div>

      {/* Conversations List */}
      <div style={styles.conversationsList}>
        {loading ? (
          // Loading skeletons
          [...Array(3)].map((_, i) => (
            <div key={i} style={themedStyles.skeletonItem}>
              <div style={themedStyles.skeletonImage} />
              <div style={styles.skeletonContent}>
                <div style={themedStyles.skeletonLine} />
                <div style={{ ...themedStyles.skeletonLine, width: '60%' }} />
                <div style={{ ...themedStyles.skeletonLine, width: '80%' }} />
              </div>
            </div>
          ))
        ) : filteredConversations.length > 0 ? (
          filteredConversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              filterColor={FILTER_CONFIG[conversation.urgency].color}
              onClick={() => handleConversationClick(conversation)}
              theme={theme}
            />
          ))
        ) : (
          <EmptyState filter={activeFilter} />
        )}
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },

  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    gap: 12,
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
  },
  headerSubtitle: {
    fontSize: 12,
    fontWeight: 500,
  },
  searchInputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    borderRadius: 8,
    padding: '8px 12px',
  },
  searchInput: {
    flex: 1,
    background: 'none',
    border: 'none',
    outline: 'none',
    fontSize: 14,
  },
  clearSearchBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchToggleBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Filter Tabs
  filterTabs: {
    display: 'flex',
    gap: 8,
    padding: '12px 20px',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  filterTab: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    borderRadius: 20,
    border: '1px solid',
    background: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  filterTabText: {
    fontSize: 12,
    fontWeight: 600,
  },
  filterTabCount: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 10,
  },

  // Conversations List
  conversationsList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 20px 100px 20px',
  },
  conversationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 12,
    marginBottom: 8,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
  },
  cardImageContainer: {
    position: 'relative',
    flexShrink: 0,
  },
  cardImage: {
    width: 50,
    height: 70,
    borderRadius: 4,
    objectFit: 'contain',
  },
  // Card Fan styles
  cardFanContainer: {
    position: 'relative',
    width: 70,
    height: 70,
    flexShrink: 0,
  },
  fannedCard: {
    position: 'absolute',
    top: 0,
    left: 0,
    transformOrigin: 'bottom left',
    boxShadow: '2px 2px 8px rgba(0,0,0,0.3)',
  },
  moreCardsIndicator: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    borderRadius: 10,
    padding: '2px 6px',
    zIndex: 10,
  },
  moreCardsText: {
    fontSize: 9,
    fontWeight: 700,
    color: '#fff',
  },
  unreadDot: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 12,
    height: 12,
    borderRadius: '50%',
  },
  conversationInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  conversationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  partnerName: {
    fontSize: 14,
    fontWeight: 600,
  },
  timeAgo: {
    fontSize: 11,
  },
  cardName: {
    fontSize: 11,
    fontWeight: 500,
  },
  lastMessage: {
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  expiryTime: {
    fontSize: 10,
    fontWeight: 600,
  },
  conversationRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  unreadBadge: {
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 6px',
  },
  unreadText: {
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
  },

  // Empty State
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  emptySubtitle: {
    fontSize: 13,
    textAlign: 'center',
  },

  // Loading Skeletons
  skeletonItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 12,
    marginBottom: 8,
  },
  skeletonImage: {
    width: 50,
    height: 70,
    borderRadius: 4,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  skeletonLine: {
    height: 12,
    borderRadius: 4,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
};

export default Messages;
